import * as Yup from 'yup';
import { unformatIban } from './functions';

export const EmailSchema = Yup.object().shape({
    email: Yup.string().required('L\'email est requis').email('L\'email saisi n\'a pas un format valide')
});

export const RegistrationNameSchema = Yup.object().shape({
    civility: Yup.string().required('Ce champ est requis'),
    lastname: Yup.string()
        .matches(/^[a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ._\s-]*$/, 'Le champ saisi ne doit contenir que des lettres'),
    birthname: Yup.string().required('Ce champ est requis')
        .matches(/^[a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ._\s-]*$/, 'Le champ saisi ne doit contenir que des lettres'),
    firstname: Yup.string().required('Ce champ est requis')
        .matches(/^[a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ._\s-]*$/, 'Le champ saisi ne doit contenir que des lettres'),
    phone: Yup.string().required('Ce champ est requis')
        .test('phone-length-for-france', '10 chiffres maximum', function(value) {
            const country = this.parent.country;
            if (country && country.phone_extension === '+33') {
                return !value || value.length <= 10;
            }
            return true;
        }),
    knownBy: Yup.string().required('Ce champ est requis')
});

export const PasswordFormSchema = Yup.object().shape({
    email: Yup.string().required('L\'email est requis').email('L\'email saisi n\'a pas un format valide'),
    password: Yup.string().required('Veuillez entrer votre mot de passe')
        .min(12, 'Au moins 12 caractères')
        .matches(/[a-z]/, 'Au moins une lettre minuscule')
        .matches(/[A-Z]/, 'Au moins une lettre majuscule')
        .matches(/[0-9]/, 'Au moins un chiffre')
        .matches(/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/, 'Au moins un caractère spécial')
        .matches(/^(?!.*(.)\1\1\1.*).*$/, ('Maximum 3 caractères identiques de suite')),
    confirmPassword: Yup.string().required('Veuillez resaisir votre mot de passe')
        .oneOf([Yup.ref('password')], 'Les deux mots de passe ne sont pas identiques'),
});

const get18YearsAgoDate = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today;
};
const get120YearsAgoDate = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 120);
    return today;
};
export const birthSchema = Yup.object().shape({
    date: Yup.string().required('La date de naissance est obligatoire')
        .matches(
            /^([0-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/\d{4}$/, 
            'Le format de la date doit être JJ/MM/AAAA'
        )
        .test('is-not-too-young', "Vous n'avez pas l'âge requis, merci de contacter nos services.", (value) => {
            if (!value) return false;
            const [day, month, year] = value.split('/');
            const birthDate = new Date(Number(year), Number(month) - 1, Number(day));
            const eighteenYearsAgo = get18YearsAgoDate();
            return birthDate <= eighteenYearsAgo;
        })
        .test('is-not-too-old', "L'âge saisi semble incorrect.", (value) => {
            if (!value) return true;
            const [day, month, year] = value.split('/');
            const birthDate = new Date(Number(year), Number(month) - 1, Number(day));
            const oneHundredTwentyYearsAgo = get120YearsAgoDate();
            return birthDate >= oneHundredTwentyYearsAgo;
        }),
    place: Yup.object().shape({
        zipcode: Yup.string().required('Le code postal est obligatoire'),
        country: Yup.string().required('Le pays est obligatoire')
    })
});

export const companySchema = Yup.object().shape({
    company_name: Yup.string()
        .matches(/^[\wáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ\s.&'-]+$/, 'Le champ saisi ne doit contenir que des chiffres ou des lettres')
})

export const sirenSchema = Yup.object().shape({
    siren: Yup.string()
        .matches(/^\d{9}$/, 'Le numéro Siren doit comporter exactement 9 chiffres')
})

export const leiSchema = Yup.object().shape({
    lei: Yup.string()
        .matches(/^[A-Za-z0-9]+$/, 'Le numéro LEI ne doit contenir ni espaces ni caractères spéciaux')
        .length(20, 'Le numéro LEI doit contenir exactement 20 caractères')
});

export const compagnyInformationsSchema = Yup.object().shape({
    name: Yup.string().required('Champs requis'),
    legal_form: Yup.string().required('Champs requis'),
    created_at: Yup.date()
        .transform((value, originalValue) => {
            if (typeof originalValue === 'string') {
                return new Date(originalValue.split('/').reverse().join('-'));
            }
            return value;
        })
        .required('Champs requis'),
    rcs: Yup.string().required('Champs requis'),
    ape: Yup.string().required('Champs requis'),
    siret: Yup.string().required('Champs requis'),
    address: Yup.object().shape({
        streetName: Yup.string().required('Champs requis'),
        streetNumber: Yup.string().required('Champs requis'),
        zipCode: Yup.string().required('Champs requise'),
        city: Yup.string().required('Champs requis'),
        country: Yup.string().required('Champs requis'),
    })
});

const maxAmountValidator = (errorMessage: string = 'Le montant ne doit pas dépasser 500 000 000') => ({
    name: 'maxValue',
    message: errorMessage,
    test: (value: any) => {
        if (!value) return true;
        const normalizedValue = value.toString().replace(',', '.');
        return Number(normalizedValue) <= 500000000;
    }
});
export const revenueSchema = Yup.object().shape({
    current: Yup.object().shape({
        revenue: Yup.string()
            .required('Le champ chiffre d\'affaires est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ chiffre d\'affaires doit être un nombre')
            .test(maxAmountValidator()),
        capital: Yup.string()
            .required('Le champ capital est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ capital doit être un nombre')
            .test(maxAmountValidator()),
        equity: Yup.string()
            .required('Le champ capitaux propres est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ capitaux propres doit être un nombre')
            .test(maxAmountValidator()),
        assets: Yup.string()
            .required('Le champ bilan est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ bilan doit être un nombre')
            .test(maxAmountValidator()),
        cash: Yup.string()
            .required('Le champ disponibilités est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ disponibilités doit être un nombre')
            .test(maxAmountValidator()),
    }),
    previous: Yup.object().shape({
        revenue: Yup.string()
            .required('Le champ chiffre d\'affaires est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ chiffre d\'affaires doit être un nombre')
            .test(maxAmountValidator()),
        capital: Yup.string()
            .required('Le champ capital est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ capital doit être un nombre')
            .test(maxAmountValidator()),
        equity: Yup.string()
            .required('Le champ capitaux propres est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ capitaux propres doit être un nombre')
            .test(maxAmountValidator()),
        assets: Yup.string()
            .required('Le champ bilan est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ bilan doit être un nombre')
            .test(maxAmountValidator()),
        cash: Yup.string()
            .required('Le champ disponibilités est requis')
            .matches(/^\d+([,.]\d{1,2})?$/, 'Le champ disponibilités doit être un nombre')
            .test(maxAmountValidator()),
    })
});

export const ibanSchema = Yup.object().shape({
    iban: Yup.string()
        .transform((value) => unformatIban(value))
        .required('L’IBAN est requis')
        .matches(/^[A-Z]{2}[A-Z0-9]*$/, 'Le format de l\'IBAN est invalide')
});

export const beneficiarySchema = Yup.object().shape({
    person: Yup.object().shape({
        civility: Yup.string().required('La civilité est obligatoire'),
        firstname: RegistrationNameSchema.fields.firstname,
        lastname: Yup.string()
            .required('Ce champ est requis')
            .matches(/^[a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ._\s-]*$/, 'Le champ saisi ne doit contenir que des lettres'),
        birthdate: Yup.string()
            .required('La date de naissance est obligatoire')
            .matches(
                /^([0-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/\d{4}$/, 
                'Le format de la date doit être JJ/MM/AAAA'
            ),
        residence_country: Yup.string().required('Le pays de résidence est obligatoire'),
        address: Yup.object().shape({
            street_number: Yup.string(),
            street_name: Yup.string().required('Le nom de rue est obligatoire'),
            street_name_further: Yup.string(),
            city: Yup.object().shape({
                zipcode: Yup.string().required('Le code postal est obligatoire'),
                name: Yup.string().required('La ville est obligatoire'),
            }),
        }),
        phone: Yup.object().shape({
            number: Yup.string()
                .required('Le numéro de téléphone est obligatoire')
                .matches(/^\d+$/, 'Le numéro de téléphone ne doit contenir que des chiffres')
                .test('phone-length-for-france', '10 chiffres maximum', function(value) {
                    const country = this.parent.country;
                    if (country && country.phone_extension === '+33') {
                        return !value || value.length <= 10;
                    }
                    return true;
                })
        }),
        email: EmailSchema.fields.email,
    }),
    shareholding: Yup.string()
        .required('Le pourcentage de détention est obligatoire')
        .matches(/^\d+([,.]\d{1,2})?$/, 'Le pourcentage doit être un nombre'),
});

export const addressesSchema = Yup.object().shape({
    street_name: Yup.string().required('Le nom de rue est obligatoire'),
    city: Yup.object().shape({
        zipcode: Yup.string().required('Le code postal est obligatoire'),
        name: Yup.string().required('La ville est obligatoire'),
    }),
});
